import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { uid } from 'react-uid';
import {
  Box,
  Stack,
  Typography,
  CircularProgress,
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import StatusContainer from './StatusContainer';
import LinkButton from './LinkButton';
import { handleFileDownload, handleDownloadK1File } from '../helpers/handleFileDownload';
import formatDate from '../../helpers/formatDate';

export default function MobileDocuments({
  documentsData,
  currentManagedUserId,
  isLoading,
  isError,
}) {
  const [loadingDocuments, setLoadingDocuments] = useState({});

  const sortedDocuments = useMemo(() => {
    const docsWithUid = documentsData.map((doc) => ({
      ...doc,
      uniqueId: uid(doc),
    }));
    docsWithUid.sort((a, b) => {
      const aDate = a.publicationDate ? new Date(a.publicationDate) : null;
      const bDate = b.publicationDate ? new Date(b.publicationDate) : null;
      if (!aDate && !bDate) { return 0; }
      if (!aDate) { return -1; } // a is missing a date → put it first
      if (!bDate) { return 1; } // b is missing a date → put it first
      return bDate - aDate; // descending order for those with dates
    });
    return docsWithUid;
  }, [documentsData]);

  const handleViewClick = (doc, e) => {
    e.stopPropagation();
    const isDocLoading = doc.fileKey ? loadingDocuments[doc.fileKey] : loadingDocuments[doc.id];
    if (isDocLoading) { return; }
    if (doc.fileKey) {
      handleDownloadK1File(doc.fileKey, currentManagedUserId, setLoadingDocuments);
    } else {
      handleFileDownload(doc.id, currentManagedUserId, setLoadingDocuments);
    }
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress size={20} sx={{ color: 'purple' }} />
      </Box>
    );
  }

  if (isError) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <Typography variant="tableMessage">
          Something went wrong. Please try again.
        </Typography>
      </Box>
    );
  }

  if (!sortedDocuments.length) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <Typography variant="tableMessage">No Documents To Display</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {sortedDocuments.map((doc) => {
        const { uniqueId, status, fileKey } = doc;
        const isDocLoading = fileKey ? loadingDocuments[fileKey] : loadingDocuments[doc.id];

        return (
          <Box
            key={uniqueId}
            sx={{
              border: '2px solid #e0e0e0',
              p: 2,
              borderRadius: 2,
              transition: 'all 0.2s ease-in-out',
              cursor: 'default',
              opacity: isDocLoading ? 0.6 : 1,
            }}
          >
            <Stack direction="column" spacing={1} mb={1}>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="body2">
                  {doc.publicationDate ? formatDate(doc.publicationDate) : '\u00A0'}
                </Typography>
                {status === 'Pending' ? (
                  <StatusContainer status={status}>Not Yet Available</StatusContainer>
                ) : isDocLoading ? (
                  <CircularProgress size={20} sx={{ color: 'purple' }} />
                ) : (
                  <LinkButton
                    variant="text"
                    onClick={(e) => handleViewClick(doc, e)}
                    sx={{ minWidth: 'auto' }}
                  >
                    View
                    <OpenInNewIcon sx={{ fontSize: 16, ml: 1.5 }} />
                  </LinkButton>
                )}
              </Stack>
            </Stack>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              {doc.title}
            </Typography>
            <Typography variant="body2">
              {doc.vintageFundNames
                ? (() => {
                  const displayNames = doc.vintageFundNames.slice(0, 5);
                  return (
                    <>
                      {displayNames.map((vintageFundName, idx) => (
                        <span key={vintageFundName}>
                          {vintageFundName}
                          {idx !== displayNames.length - 1 && <br />}
                        </span>
                      ))}
                      {doc.vintageFundNames.length > 6 && <span>...</span>}
                    </>
                  );
                })()
                : doc.fundName || '-'}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
}

MobileDocuments.propTypes = {
  documentsData: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      title: PropTypes.string.isRequired,
      fileName: PropTypes.string,
      fundName: PropTypes.string,
      vintageFundNames: PropTypes.arrayOf(PropTypes.string),
      publicationDate: PropTypes.string,
      status: PropTypes.oneOf(['Pending', 'Available']),
      year: PropTypes.number,
      fileKey: PropTypes.string,
      documentType: PropTypes.string,
    }),
  ).isRequired,
  currentManagedUserId: PropTypes.number,
  isLoading: PropTypes.bool,
  isError: PropTypes.bool,
};

MobileDocuments.defaultProps = {
  currentManagedUserId: null,
  isLoading: false,
  isError: false,
};
